import { AlertTriangle, Loader2 } from "lucide-react";
import type * as React from "react";
import { ApiError } from "@/lib/api";
import { useToasts } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { errorText, zh } from "@/i18n/zh";
import { Button } from "./ui/button";

export function PageHeader({ title, extra, sub }: { title: string; extra?: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        {sub ? <div className="mt-1 text-sm text-muted-foreground">{sub}</div> : null}
      </div>
      {extra ? <div className="flex flex-wrap items-center gap-2">{extra}</div> : null}
    </div>
  );
}

export function Loading({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-2 p-6 text-sm text-muted-foreground", className)}>
      <Loader2 className="size-4 animate-spin" /> {zh.common.loading}
    </div>
  );
}

export function Empty({ text = zh.common.empty }: { text?: string }) {
  return <div className="p-6 text-center text-sm text-muted-foreground">{text}</div>;
}

export function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403 && e.code === "authz.denied") return zh.errors.forbidden;
    return errorText(e.code, e.message);
  }
  if (e instanceof TypeError) return zh.errors.network;
  if (e instanceof Error) return errorText(e.message, e.message);
  return zh.errors.title;
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
      <AlertTriangle className="size-4 text-destructive" />
      <span className="flex-1">{describeError(error)}</span>
      {onRetry ? (
        <Button size="sm" variant="outline" onClick={onRetry}>
          {zh.common.retry}
        </Button>
      ) : null}
    </div>
  );
}

export function Toaster() {
  const items = useToasts((s) => s.items);
  return (
    <div className="pointer-events-none fixed top-4 left-1/2 z-[60] flex -translate-x-1/2 flex-col items-center gap-2" aria-live="polite">
      {items.map((t) => (
        <div
          key={t.id}
          role="status"
          className={cn(
            "pointer-events-auto rounded-md border px-4 py-2 text-sm shadow-md",
            t.tone === "error" && "border-destructive/50 bg-destructive text-destructive-foreground",
            t.tone === "success" && "bg-card",
            t.tone === "info" && "bg-card",
          )}
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function KV({ items }: { items: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
      {items.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="min-w-0 break-all">{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}
